import {
  Injectable,
  ApplicationRef,
  EnvironmentInjector,
  Inject,
  createComponent,
  ComponentRef,
} from '@angular/core';
import { DialogModalComponent } from '../../shared/components/modal/dialog-modal.component';

export interface DialogAlertOptions {
  title: string;
  body: string;
  buttonText?: string;
  variant?: 'info' | 'danger';
}

export interface DialogConfirmOptions {
  title: string;
  body: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'info' | 'danger';
}

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private modalRef?: ComponentRef<DialogModalComponent>;
  private queue: Array<() => void> = [];
  private active = false;

  constructor(
    @Inject(ApplicationRef) private appRef: ApplicationRef,
    @Inject(EnvironmentInjector) private injector: EnvironmentInjector
  ) {}

  alert(options: DialogAlertOptions): Promise<void> {
    return new Promise((resolve) => {
      this.enqueue(() => {
        this.showDialog({
          title: options.title,
          body: options.body,
          confirmText: options.buttonText ?? 'OK',
          variant: options.variant ?? 'info',
          showCancel: false,
        }, () => resolve());
      });
    });
  }

  confirm(options: DialogConfirmOptions): Promise<boolean> {
    return new Promise((resolve) => {
      this.enqueue(() => {
        this.showDialog({
          title: options.title,
          body: options.body,
          confirmText: options.confirmText ?? 'OK',
          cancelText: options.cancelText ?? 'Avbryt',
          variant: options.variant ?? 'info',
          showCancel: true,
        }, (confirmed) => resolve(confirmed));
      });
    });
  }

  private enqueue(operation: () => void): void {
    if (this.active) {
      this.queue.push(operation);
      return;
    }

    operation();
  }

  private showDialog(
    options: {
      title: string;
      body: string;
      confirmText: string;
      cancelText?: string;
      variant: 'info' | 'danger';
      showCancel: boolean;
    },
    onClose: (confirmed: boolean) => void
  ): void {
    this.active = true;

    this.modalRef = createComponent(DialogModalComponent, {
      environmentInjector: this.injector,
    });

    this.modalRef.instance.title = options.title;
    this.modalRef.instance.body = options.body;
    this.modalRef.instance.confirmText = options.confirmText;
    this.modalRef.instance.cancelText = options.cancelText ?? 'Avbryt';
    this.modalRef.instance.variant = options.variant;
    this.modalRef.instance.showCancel = options.showCancel;

    this.appRef.attachView(this.modalRef.hostView);
    const domElem = (this.modalRef.hostView as any).rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);
    this.modalRef.instance.show();

    const subscription = this.modalRef.instance.onClose.subscribe((confirmed: boolean) => {
      subscription.unsubscribe();
      this.removeModal();
      this.active = false;
      onClose(confirmed);
      this.runNext();
    });
  }

  private runNext(): void {
    const next = this.queue.shift();
    if (next) next();
  }

  private removeModal(): void {
    if (!this.modalRef) {
      return;
    }

    this.appRef.detachView(this.modalRef.hostView);
    this.modalRef.destroy();
    this.modalRef = undefined;
  }
}
